/**
 * หน้าจอใบสั่งผลิต — รายการ / รายละเอียด / เปิดใบใหม่
 *
 * การสั่งปล่อยอยู่บนหน้ารายละเอียด (`ProductionOrderReleasePanel`) เพราะมันเป็น
 * คำสั่งที่ผูกกับใบใดใบหนึ่ง และผู้ใช้ต้องเห็นสิ่งที่กำลังจะถูกตรึงไว้ก่อนกด ส่วน
 * `complete`/`close`/`cancel` อยู่ที่ `ProductionOrderTransitionPanel`
 *
 * การ gate สิทธิ์เป็นแบบเดียวกับที่ `WO-P1-M10-001` วางไว้: อ่าน flag จาก `modules`
 * ของ permission set ที่เซิร์ฟเวอร์ส่งมาเท่านั้น ผ่าน `can()` **ห้าม derive กติกา
 * ADMIN/AUDITOR ซ้ำจาก `system_roles`** และทุกสถานะที่ไม่ใช่ `ready` แปลว่าไม่มีสิทธิ์
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย การซ่อนปุ่ม "เปิดใบใหม่" ไม่ได้
 * ป้องกันอะไรเลย — เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ ที่ซ่อนเพราะไม่อยากให้
 * ผู้ใช้กรอกฟอร์มแล้วโดนปฏิเสธตอนกดบันทึก
 */

import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { ProductionOrderCreateView } from './ProductionOrderCreateView';
import { ProductionOrderDetailView } from './ProductionOrderDetailView';
import { ProductionOrderListView } from './ProductionOrderListView';
import {
  hasCrossPlantScope,
  orderCreateSearch,
  orderDetailSearch,
  orderListSearch,
  resolveProductionOrderCreatePrefill,
  resolveProductionOrderListFilterPrefill,
  resolveProductionOrderView,
  selectablePlants,
} from './productionOrderView';
import { StatusBadge } from '../StatusBadge';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import { PRODUCTION_ORDER_MODULE } from '../../lib/productionOrders/types';

export interface ProductionOrderScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

function CreateNotOfferedNotice() {
  return (
    <div className="cap-page">
      <div className="cap-notice" data-testid="production-order-create-unavailable" role="status">
        <StatusBadge tone="warning">ไม่ได้เปิดฟอร์มให้</StatusBadge>
        <h1 className="cap-notice__title">สิทธิ์ชุดนี้ไม่มีการเปิดใบสั่งผลิต</h1>
        <p className="cap-notice__text">
          สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_create</code>{' '}
          ของโมดูล <code className="cap-table__code">PRODUCTION_ORDER</code>{' '}
          หน้าจอนี้จึงไม่เปิดฟอร์มให้กรอก
        </p>
        <p className="cap-notice__note">
          การไม่เปิดฟอร์มเป็นเพียง UX เพื่อไม่ให้กรอกงานที่จะถูกปฏิเสธตอนบันทึก —
          ผู้ตัดสินจริงคือ backend ทุกคำขอเสมอ
        </p>
      </div>
    </div>
  );
}

export function ProductionOrderScreen({
  permissions,
  apiBaseUrl,
  fetchImpl,
}: ProductionOrderScreenProps) {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const view = resolveProductionOrderView(searchParams);
  const canCreate = can(permissions, PRODUCTION_ORDER_MODULE, 'CREATE');
  const canApprove = can(permissions, PRODUCTION_ORDER_MODULE, 'APPROVE');
  const canEdit = can(permissions, PRODUCTION_ORDER_MODULE, 'EDIT');

  // โรงงานมาจาก `plant_scope` ที่เซิร์ฟเวอร์ส่งมาเท่านั้น ไม่ใช่รายการที่หน้าจอคิดเอง
  const plants = selectablePlants(permissions);
  const crossPlant = hasCrossPlantScope(permissions);

  if (view.kind === 'create') {
    if (!canCreate) {
      return <CreateNotOfferedNotice />;
    }
    return (
      <ProductionOrderCreateView
        apiBaseUrl={apiBaseUrl}
        fetchImpl={fetchImpl}
        plants={plants}
        prefill={resolveProductionOrderCreatePrefill(searchParams, plants)}
        listHref={orderListSearch()}
        onCreated={(order) => {
          // ไปที่ใบที่เพิ่งเปิด แบบ replace เพื่อให้ปุ่ม back ไม่ย้อนกลับไปที่ฟอร์ม
          // ซึ่งถูกบันทึกไปแล้ว (แล้วชวนให้กดบันทึกซ้ำจนได้ใบซ้ำ)
          navigate(
            { pathname, search: orderDetailSearch(order.production_order_id) },
            { replace: true },
          );
        }}
      />
    );
  }

  if (view.kind === 'detail') {
    return (
      <ProductionOrderDetailView
        orderId={view.orderId}
        apiBaseUrl={apiBaseUrl}
        fetchImpl={fetchImpl}
        canApprove={canApprove}
        canEdit={canEdit}
        listHref={orderListSearch()}
      />
    );
  }

  return (
    <ProductionOrderListView
      apiBaseUrl={apiBaseUrl}
      fetchImpl={fetchImpl}
      plants={plants}
      crossPlant={crossPlant}
      canCreate={canCreate}
      createHref={orderCreateSearch()}
      filterPrefill={resolveProductionOrderListFilterPrefill(searchParams, plants)}
    />
  );
}
